<template>
  <div class="google-maps-loader">
    <div class="google-maps-loader__placeholder" ref="googleMaps" />
    <template v-if="Boolean(this.google) && Boolean(this.map)">
      <slot name="markers" />
      <slot />
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import {
  MAP_CONFIG,
} from '@/config/google-maps.config';
import isMobile from '../../../helpers/platform';

export default {
  name: 'GoogleMapsLoader',

  props: {
    /**
     * Should all markers initially be visible
     */
    allMarkersInView: {
      type: Boolean,
      required: false,
      default: true,
    },

    /**
     * Set selected marker in the center of the map
     */
    markerInCenter: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      google: null,
      map: null,
      maxZoom: MAP_CONFIG.maxZoom || 15,
    };
  },

  computed: {
    ...mapState({
      markers: (state) => state.markers.markers,
      selectedMarker: (state) => state.markers.selected,
      selectedRegion: (state) => state.dataLayer.selected,
      feature: (state) => state.dataLayer.feature,
    }),
    ...mapGetters({
      googleApi: 'google/getGoogleMapsApi',
    }),
  },

  watch: {
    markers() {
      if (!this.$scopedSlots.markers().length) {
        return;
      }

      if (this.allMarkersInView) {
        this.setBounding();
      }
    },

    selectedMarker(value) {
      if (value !== null && this.markerInCenter) {
        this.map.setZoom(this.maxZoom);
        this.map.panTo(this.selectedMarker.getPosition());

        return;
      }

      if (this.allMarkersInView) {
        this.setBounding();
      }
    },

    selectedRegion(value) {
      if (value !== null && this.markerInCenter) {
        const bounds = new this.google.maps.LatLngBounds();

        this.processPoints(this.feature.getGeometry(), bounds.extend, bounds);
        this.map.fitBounds(bounds);

        // pan the map so that the selected region is better visible
        // and is less hidden by the overlaying region popup
        if (isMobile()) {
          this.map.panBy(0, 115);
        } else {
          this.map.panBy(-250, 50);
        }

        return;
      }

      this.setBounding();
    },
  },

  async mounted() {
    this.googleApi.then((result) => {
      this.google = result;

      this.$store.commit('google/setApi', this.google);

      this.initializeMap();
    });
  },

  methods: {
    processPoints(geometry, callback, thisArg) {
      if (geometry instanceof this.google.maps.LatLng) {
        callback.call(thisArg, geometry);
      } else if (geometry instanceof this.google.maps.Data.Point) {
        callback.call(thisArg, geometry.get());
      } else {
        geometry.getArray().forEach((g) => {
          this.processPoints(g, callback, thisArg);
        });
      }
    },

    initializeMap() {
      const mapContainer = this.$refs.googleMaps;

      this.map = new this.google.maps.Map(
        mapContainer,
        {
          ...MAP_CONFIG,
          center: new this.google.maps.LatLng(51.8059068, 5.2217737),
        },
      );

      this.map.setZoom(this.map.minZoom);

      this.$store.commit('google/setMap', this.map);

      this.setBounding();
    },

    setBounding() {
      let bounds = new this.google.maps.LatLngBounds();

      // Set bounding of netherlands
      if (!this.$scopedSlots.markers) {
        const swBound = new this.google.maps.LatLng(50.803721015, 3.31497114423);
        const neBound = new this.google.maps.LatLng(53.5104033474, 7.09205325687);

        bounds = new this.google.maps.LatLngBounds(swBound, neBound);
      } else {
        // set bounding of all markers
        this.markers.forEach((marker) => {
          bounds.extend(marker.getPosition());
        });
      }

      this.map.fitBounds(bounds);
    },
  },
};
</script>

<style lang="scss" scoped>
  // stylelint-disable declaration-no-important, selector-max-universal
  .google-maps-loader {
    display: flex;
    height: 100%;
    left: 0;
    position: fixed;
    top: 0;
    width: 100%;

    &__placeholder {
      width: 100%;
    }
  }
</style>
